import { type JsonMessage, type Room, type Transport, asText, randomId } from './transport';

/**
 * Parties (research 04 §5.2): up to four players sharing a password-protected
 * room that works across zones. Members are keyed by a stable character uid so
 * a reloaded page (new peer id) keeps its place. The leader owns the member
 * order, kicks, promotions and the ready check that takes everyone into an
 * instance together (the leader then hosts it).
 */

export const PARTY_MAX = 4;
const HEARTBEAT_MS = 2000;
const STATE_MS = 5000;
const READY_MS = 20000;
/** How long an offline leader keeps the role before the next member takes over. */
const LEADER_GRACE_MS = 10000;
/** A rejoining member with no leader in sight claims leadership after this long. */
const ORPHAN_MS = 6000;

export interface MemberInfo {
  uid: string;
  name: string;
  classId: string;
  level: number;
  ilvl: number;
  zone: string | null;
  hpPct: number;
  alive: boolean;
}

export interface PartyMember extends MemberInfo {
  /** Current peer id, or null while offline. */
  peer: string | null;
  seen: number;
  offlineSince: number;
}

export interface EnterInfo {
  zone: string;
  inst: string;
  pw: string;
  host: string;
  size: number;
  opts: Record<string, string | number | boolean>;
}

export interface ReadyState {
  rid: string;
  zone: string;
  label: string;
  opts: Record<string, string | number | boolean>;
  expires: number;
  leaderUid: string;
  responses: Map<string, boolean | null>;
  mine: boolean | null;
}

export interface PartyDeps {
  transport: Transport;
  self(): MemberInfo;
  /** Wall clock in ms. */
  now(): number;
  changed(): void;
  enter(info: EnterInfo): void;
  notify(text: string): void;
  chat(fromName: string, text: string, fromPeer: string): void;
  /** Remembers membership for reconnection (null after leaving). */
  persist?(p: { id: string; key: string } | null): void;
  /** Messages the party protocol does not use (whispers, trade). */
  other?(from: string, m: JsonMessage): void;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 && v.length < 64 ? v : null);

function sanitizeMember(m: unknown): MemberInfo | null {
  if (!m || typeof m !== 'object') return null;
  const o = m as Record<string, unknown>;
  const uid = str(o.uid);
  if (!uid) return null;
  return {
    uid,
    name: asText(o.name, '?', 24),
    classId: asText(o.classId),
    level: Number(o.level) || 1,
    ilvl: Number(o.ilvl) || 0,
    zone: typeof o.zone === 'string' ? o.zone : null,
    hpPct: Math.max(0, Math.min(1, Number(o.hpPct) || 0)),
    alive: o.alive !== false,
  };
}

export class PartyManager {
  id: string | null = null;
  key: string | null = null;
  /** Leader uid (null until known). */
  leader: string | null = null;
  /** Leadership epoch: bumped on every hand-over so competing claims resolve the same way everywhere. */
  epoch = 0;
  order: string[] = [];
  readonly members = new Map<string, PartyMember>();
  room: Room | null = null;
  ready: ReadyState | null = null;
  private offs: (() => void)[] = [];
  private lastBeat = 0;
  private lastState = 0;
  private joinedAt = 0;

  constructor(private d: PartyDeps) {}

  get inParty(): boolean {
    return this.id !== null;
  }

  get selfUid(): string {
    return this.d.self().uid;
  }

  get isLeader(): boolean {
    return this.inParty && this.leader === this.selfUid;
  }

  /** Members currently connected (including us). */
  onlineMembers(): PartyMember[] {
    return this.order.map((u) => this.members.get(u)).filter((m): m is PartyMember => !!m && m.peer !== null);
  }

  leaderPeer(): string | null {
    if (!this.leader) return null;
    return this.members.get(this.leader)?.peer ?? null;
  }

  memberByPeer(peer: string): PartyMember | undefined {
    for (const m of this.members.values()) if (m.peer === peer) return m;
    return undefined;
  }

  /** Starts a new party led by us. */
  async create(): Promise<void> {
    await this.join(randomId(12), randomId(16), true);
  }

  /** Joins (or rejoins) a party room. */
  async join(id: string, key: string, asLeader = false): Promise<void> {
    if (this.id === id) return;
    if (this.id) this.leave();
    const now = this.d.now();
    const me = this.d.self();
    this.id = id;
    this.key = key;
    this.members.clear();
    this.members.set(me.uid, { ...me, peer: this.d.transport.selfId, seen: now, offlineSince: 0 });
    this.order = [me.uid];
    this.leader = asLeader ? me.uid : null;
    this.epoch = asLeader ? 1 : 0;
    this.joinedAt = now;
    this.d.persist?.({ id, key });
    this.d.changed();
    const room = await this.d.transport.join(`p:${id}`, { password: key });
    if (this.id !== id) {
      void room.leave();
      return;
    }
    this.room = room;
    this.offs.push(
      room.onMessage((from, m) => this.onMessage(from, m)),
      room.onPeerJoin((peer) => {
        this.send({ t: 'pm', m: this.d.self() }, peer);
        if (this.isLeader) this.sendState(peer);
      }),
      room.onPeerLeave((peer) => this.onPeerLeave(peer)),
    );
    this.beat(true);
  }

  leave(): void {
    if (!this.id) return;
    this.send({ t: 'pleave', uid: this.selfUid });
    for (const off of this.offs) off();
    this.offs = [];
    void this.room?.leave();
    this.room = null;
    this.id = null;
    this.key = null;
    this.leader = null;
    this.epoch = 0;
    this.order = [];
    this.members.clear();
    this.ready = null;
    this.d.persist?.(null);
    this.d.changed();
  }

  /** Invitation payload (sent through any room the invitee shares with us). */
  inviteMessage(): JsonMessage | null {
    if (!this.id || !this.key) return null;
    return { t: 'pinv', pid: this.id, key: this.key, leader: this.d.self().name, size: this.order.length };
  }

  kick(uid: string): void {
    if (!this.isLeader || uid === this.selfUid) return;
    this.send({ t: 'pkick', uid });
    this.removeMember(uid);
    this.sendState();
  }

  promote(uid: string): void {
    if (!this.isLeader || !this.members.has(uid)) return;
    this.leader = uid;
    this.epoch++;
    this.lastState = this.d.now();
    this.send({ t: 'pst', leader: uid, order: this.order, ep: this.epoch });
    this.d.changed();
  }

  chat(text: string): void {
    this.send({ t: 'pchat', name: this.d.self().name, text });
  }

  // ---------------------------------------------------------------- ready check

  /** Leader: asks online members to enter `zone` together. Returns false when solo. */
  startReadyCheck(zone: string, label: string, opts: Record<string, string | number | boolean>): boolean {
    if (!this.isLeader) return false;
    const others = this.onlineMembers().filter((m) => m.uid !== this.selfUid);
    if (others.length === 0) return false;
    const responses = new Map<string, boolean | null>();
    responses.set(this.selfUid, true);
    for (const m of others) responses.set(m.uid, null);
    const rid = randomId(8);
    const expires = this.d.now() + READY_MS;
    this.ready = { rid, zone, label, opts, expires, leaderUid: this.selfUid, responses, mine: true };
    this.send({ t: 'prc', rid, zone, label, opts, exp: expires });
    this.d.changed();
    return true;
  }

  answerReadyCheck(ok: boolean): void {
    const r = this.ready;
    if (!r || r.mine !== null) return;
    r.mine = ok;
    r.responses.set(this.selfUid, ok);
    const leaderPeer = this.leaderPeer();
    if (leaderPeer) this.send({ t: 'prc_r', rid: r.rid, uid: this.selfUid, ok }, leaderPeer);
    if (!ok) this.ready = null;
    this.d.changed();
  }

  cancelReadyCheck(): void {
    if (!this.ready) return;
    if (this.isLeader) this.send({ t: 'prc_x', rid: this.ready.rid });
    this.ready = null;
    this.d.changed();
  }

  private finishReadyCheck(): void {
    const r = this.ready;
    if (!r || !this.isLeader) return;
    this.ready = null;
    const accepted = [...r.responses].filter(([, ok]) => ok === true).map(([uid]) => uid);
    const declined = [...r.responses].filter(([, ok]) => ok !== true).map(([uid]) => this.members.get(uid)?.name ?? '?');
    if (declined.length) this.d.notify(`Not joining: ${declined.join(', ')}`);
    const info: EnterInfo = { zone: r.zone, inst: randomId(12), pw: randomId(12), host: this.d.transport.selfId, size: accepted.length, opts: r.opts };
    const peers = accepted.map((u) => this.members.get(u)?.peer).filter((p): p is string => !!p && p !== this.d.transport.selfId);
    if (peers.length) this.send({ t: 'penter', info, members: accepted }, peers);
    this.d.changed();
    this.d.enter(info);
  }

  // ---------------------------------------------------------------- protocol

  private send(msg: JsonMessage, to?: string | readonly string[]): void {
    this.room?.send(msg, to);
  }

  private sendState(to?: string): void {
    if (!this.isLeader) return;
    this.lastState = this.d.now();
    this.send({ t: 'pst', leader: this.leader, order: this.order, ep: this.epoch }, to);
  }

  private beat(force = false): void {
    const now = this.d.now();
    if (!force && now - this.lastBeat < HEARTBEAT_MS) return;
    this.lastBeat = now;
    const me = this.d.self();
    const mine = this.members.get(me.uid);
    if (mine) Object.assign(mine, me, { seen: now });
    this.send({ t: 'pm', m: me });
  }

  private claimLeadership(order: string[]): void {
    this.leader = this.selfUid;
    this.epoch++;
    this.order = [this.selfUid, ...order.filter((u) => u !== this.selfUid)].slice(0, PARTY_MAX);
    this.sendState();
    this.d.changed();
  }

  private removeMember(uid: string): void {
    this.order = this.order.filter((u) => u !== uid);
    this.members.delete(uid);
    this.ready?.responses.delete(uid);
    if (this.leader === uid) this.leader = this.order[0] ?? null;
    this.d.changed();
  }

  private onPeerLeave(peer: string): void {
    const m = this.memberByPeer(peer);
    if (!m) return;
    m.peer = null;
    m.offlineSince = this.d.now();
    if (this.ready?.responses.get(m.uid) === null) this.ready.responses.set(m.uid, false);
    this.d.changed();
  }

  private isFromLeader(from: string): boolean {
    const m = this.memberByPeer(from);
    return !!m && m.uid === this.leader;
  }

  private onMessage(from: string, msg: JsonMessage): void {
    const now = this.d.now();
    switch (msg.t) {
      case 'pm': {
        const info = sanitizeMember(msg.m);
        if (!info || info.uid === this.selfUid) return;
        // A uid moving to a new peer id (page reload) replaces the old mapping.
        this.members.set(info.uid, { ...info, peer: from, seen: now, offlineSince: 0 });
        if (this.isLeader && !this.order.includes(info.uid)) {
          if (this.order.length < PARTY_MAX) {
            this.order.push(info.uid);
            this.sendState();
          } else {
            this.members.delete(info.uid);
            this.send({ t: 'pfull' }, from);
          }
        }
        this.d.changed();
        return;
      }
      case 'pst': {
        const leader = str(msg.leader);
        const order = Array.isArray(msg.order) ? msg.order.filter((u): u is string => typeof u === 'string').slice(0, PARTY_MAX) : null;
        const sender = this.memberByPeer(from);
        const ep = Number(msg.ep) || 0;
        if (!leader || !order || !sender) return;
        // Current leader (or its promotion of someone else); competing claims: newer epoch, then lower uid, wins.
        const fromLeader = sender.uid === leader || sender.uid === this.leader;
        const newer = ep > this.epoch || (ep === this.epoch && (this.leader === null || leader <= this.leader));
        if (!fromLeader || !newer) {
          if (this.isLeader && ep <= this.epoch) this.sendState(from);
          return;
        }
        this.leader = leader;
        this.epoch = ep;
        this.order = order;
        for (const uid of [...this.members.keys()]) if (!order.includes(uid) && uid !== this.selfUid) this.members.delete(uid);
        this.d.changed();
        return;
      }
      case 'pkick': {
        if (!this.isFromLeader(from)) return;
        const uid = str(msg.uid);
        if (!uid) return;
        if (uid === this.selfUid) {
          this.leave();
          this.d.notify('You were removed from the party.');
        } else this.removeMember(uid);
        return;
      }
      case 'pleave': {
        const m = this.memberByPeer(from);
        if (m) this.removeMember(m.uid);
        if (this.isLeader) this.sendState();
        return;
      }
      case 'pfull':
        if (this.isFromLeader(from) || this.leader === null) {
          this.leave();
          this.d.notify('That party is already full.');
        }
        return;
      case 'prc': {
        if (!this.isFromLeader(from)) return;
        const rid = str(msg.rid);
        const zone = str(msg.zone);
        if (!rid || !zone) return;
        const responses = new Map<string, boolean | null>();
        for (const m of this.onlineMembers()) responses.set(m.uid, m.uid === this.leader ? true : null);
        const opts = msg.opts && typeof msg.opts === 'object' ? (msg.opts as Record<string, string | number | boolean>) : {};
        this.ready = { rid, zone, label: asText(msg.label, zone), opts, expires: Math.min(Number(msg.exp) || now + READY_MS, now + READY_MS), leaderUid: this.leader as string, responses, mine: null };
        this.d.changed();
        return;
      }
      case 'prc_r': {
        const r = this.ready;
        const uid = str(msg.uid);
        if (!r || msg.rid !== r.rid || !uid || this.memberByPeer(from)?.uid !== uid) return;
        r.responses.set(uid, msg.ok === true);
        this.d.changed();
        if (this.isLeader && [...r.responses.values()].every((v) => v !== null)) this.finishReadyCheck();
        return;
      }
      case 'prc_x':
        if (this.isFromLeader(from) && this.ready?.rid === msg.rid) {
          this.ready = null;
          this.d.changed();
        }
        return;
      case 'penter': {
        if (!this.isFromLeader(from)) return;
        const members = Array.isArray(msg.members) ? msg.members : [];
        const info = msg.info as EnterInfo | undefined;
        if (!info || !members.includes(this.selfUid) || typeof info.zone !== 'string' || typeof info.inst !== 'string') return;
        this.ready = null;
        this.d.changed();
        this.d.enter({ zone: info.zone, inst: info.inst, pw: String(info.pw ?? ''), host: from, size: Number(info.size) || members.length, opts: info.opts && typeof info.opts === 'object' ? info.opts : {} });
        return;
      }
      case 'pchat': {
        const m = this.memberByPeer(from);
        if (m && typeof msg.text === 'string') this.d.chat(m.name, msg.text.slice(0, 200), from);
        return;
      }
      default:
        this.d.other?.(from, msg);
        return;
    }
  }

  // ---------------------------------------------------------------- upkeep

  tick(): void {
    if (!this.id) return;
    const now = this.d.now();
    this.beat();
    if (this.isLeader && now - this.lastState >= STATE_MS) this.sendState();
    if (this.leader === null) {
      // Rejoined a party nobody leads (everyone reloaded): the lowest connected uid takes over.
      if (now - this.joinedAt > ORPHAN_MS) {
        const online = [...this.members.values()].filter((m) => m.peer !== null).map((m) => m.uid).sort();
        if (online[0] === this.selfUid) this.claimLeadership(online);
      }
    } else if (this.leader !== this.selfUid) {
      // An absent leader hands over to the first connected member in the leader's order.
      const leader = this.members.get(this.leader);
      if (!leader || (leader.peer === null && now - leader.offlineSince > LEADER_GRACE_MS)) {
        const next = this.order.find((u) => u !== this.leader && this.members.get(u)?.peer);
        if (next === this.selfUid) this.claimLeadership(this.order.filter((u) => u !== this.leader));
      }
    }
    if (this.ready && now > this.ready.expires) {
      if (this.isLeader) this.finishReadyCheck();
      else {
        this.ready = null;
        this.d.changed();
      }
    }
  }
}
